/**
 * MyProblemsPage — Cross-community view of all problem workspaces
 * where the current user is a lead, owner, or investigator.
 *
 * Mounted at /problems under the "Lead" left nav tier.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  fetchWorkspaces,
  WorkspaceResponse,
} from '../../api/workspaces';
import {
  fetchAllCommunityEntities,
  CommunityEntityResponse,
} from '../../api/communityMetadata';
import { mediaSrc } from '../../api/mediaUrl';
import styles from './MyProblemsPage.module.css';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface Props {
  currentUserGuid: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const MyProblemsPage: React.FC<Props> = ({ currentUserGuid }) => {
  const { t } = useAppTranslation('pages');
  const navigate = useNavigate();

  const [workspaces, setWorkspaces] = useState<WorkspaceResponse[]>([]);
  const [communityMap, setCommunityMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Load data ──────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ws, entities] = await Promise.all([
        fetchWorkspaces({ userGuid: currentUserGuid, isActive: true }),
        fetchAllCommunityEntities(),
      ]);
      setWorkspaces(ws);

      // Build communityGuid → name lookup
      const map = new Map<string, string>();
      for (const e of (entities as CommunityEntityResponse[])) {
        map.set(e.entityGuid.toUpperCase(), e.name);
      }
      setCommunityMap(map);
    } catch (e: any) {
      setError(e?.message ?? t('problems.loadError'));
    } finally {
      setLoading(false);
    }
  }, [currentUserGuid, t]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Role resolution ────────────────────────────────────────
  const resolveRole = useCallback(
    (ws: WorkspaceResponse): string => {
      const ug = currentUserGuid.toUpperCase();
      if ((ws.teamLeadGuid || '').toUpperCase() === ug) return t('problems.roleLead');
      if ((ws.createdBy || '').toUpperCase() === ug) return t('problems.roleOwner');
      return t('problems.roleInvestigator');
    },
    [currentUserGuid, t]
  );

  // ── Phase label ────────────────────────────────────────────
  const phaseLabel = useCallback(
    (phase: string): string => t(`workspace.phaseLabels.${phase}`) || phase,
    [t]
  );

  const phaseIndex = useCallback((phase: string): number => {
    const phases = [
      'signal_detection', 'detection_validation', 'analysis',
      'solution_design', 'resourcing', 'implementation', 'sustainment',
    ];
    return phases.indexOf(phase) + 1;
  }, []);

  // ── Community name resolution ──────────────────────────────
  const communityName = useCallback(
    (guid: string | null): string => {
      if (!guid) return '';
      return communityMap.get(guid.toUpperCase()) ?? '';
    },
    [communityMap]
  );

  // ── Hero URL ───────────────────────────────────────────────
  const resolveHero = useCallback((ws: WorkspaceResponse): string | null => {
    return mediaSrc(ws.detailHeroImageUrl ?? ws.heroImageUrl);
  }, []);

  // ── Navigation ─────────────────────────────────────────────
  const openWorkspace = useCallback(
    (ws: WorkspaceResponse) => {
      if (ws.communityGuid) {
        navigate(`/communities/${ws.communityGuid}/workspaces/${ws.workspaceGuid}`);
      } else {
        navigate(`/workspaces/${ws.workspaceGuid}`);
      }
    },
    [navigate]
  );

  // ── Group by community ─────────────────────────────────────
  const groupedByCommunity = useMemo(() => {
    const groups = new Map<string, { name: string; workspaces: WorkspaceResponse[] }>();
    // "No community" bucket for standalone workspaces
    const standaloneKey = '__standalone__';

    for (const ws of workspaces) {
      const key = ws.communityGuid?.toUpperCase() ?? standaloneKey;
      if (!groups.has(key)) {
        groups.set(key, {
          name: ws.communityGuid ? communityName(ws.communityGuid) : '',
          workspaces: [],
        });
      }
      groups.get(key)!.workspaces.push(ws);
    }
    return groups;
  }, [workspaces, communityName]);

  // ── Render ─────────────────────────────────────────────────

  if (loading) {
    return <div className={styles.loading}>{t('problems.title')}…</div>;
  }

  if (error) {
    return (
      <div className={`${styles.container} pageContainer`}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} pageContainer`}>
      {/* Empty state */}
      {workspaces.length === 0 && (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>{t('problems.noProblems')}</p>
          <p className={styles.emptyHint}>{t('problems.noProblemsHint')}</p>
        </div>
      )}

      {/* Workspace cards grouped by community */}
      {[...groupedByCommunity.entries()].map(([key, group]) => (
        <div key={key} className={styles.communityGroup}>
          {group.name && (
            <h3 className={styles.communityHeading}>{group.name}</h3>
          )}

          <div className={styles.cardGrid}>
            {group.workspaces.map((ws) => {
              const heroUrl = resolveHero(ws);
              const role = resolveRole(ws);
              const categoryKey = ws.concernCategory
                ? t(`workspace.categoryLabels.${ws.concernCategory}`) || ws.concernCategory
                : null;

              return (
                <button
                  key={ws.workspaceGuid}
                  type="button"
                  className={styles.card}
                  onClick={() => openWorkspace(ws)}
                >
                  {/* Hero thumbnail */}
                  <div className={styles.cardHero}>
                    {heroUrl ? (
                      <img
                        className={styles.cardHeroImage}
                        src={heroUrl}
                        alt={ws.presentingConcern}
                      />
                    ) : (
                      <div className={styles.cardHeroPlaceholder} />
                    )}
                  </div>

                  {/* Card body */}
                  <div className={styles.cardBody}>
                    {/* Badge row */}
                    <div className={styles.badgeRow}>
                      <span className={styles.phaseBadge}>
                        {phaseIndex(ws.phase)}/7 {phaseLabel(ws.phase)}
                      </span>
                      <span className={styles.roleBadge}>{role}</span>
                      {categoryKey && (
                        <span className={styles.categoryBadge}>{categoryKey}</span>
                      )}
                    </div>

                    {/* Title */}
                    <h4 className={styles.cardTitle}>
                      {ws.title || ws.presentingConcern}
                    </h4>

                    {/* Description */}
                    {ws.description && (
                      <p className={styles.cardDescription}>{ws.description}</p>
                    )}

                    {/* Stats */}
                    <div className={styles.cardStats}>
                      <span>{t('workspace.teamMembers', { count: ws.teamMemberCount })}</span>
                      <span>{t('workspace.evidenceCount', { count: ws.evidenceCount })}</span>
                      <span>{t('workspace.solutionCount', { count: ws.solutionCount })}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default MyProblemsPage;
